import type { Me, PrefsOut, CompatPrefsOut } from '../api/endpoints';
import type { Plan } from '../contexts/UserContext';
import type { TrackedJob } from '../contexts/JobsContext';

export interface DynamicContextInput {
  user: Me | null;
  plan: Plan;
  scannerPrefs: PrefsOut | null;
  compatPrefs: CompatPrefsOut | null;
  jobs: TrackedJob[];
  /** Override "now" for tests / determinism. Defaults to current time. */
  now?: Date;
}

/** Compact prefs into a small JSON object. Only includes set / non-default keys to save tokens. */
function compactPrefs(p: PrefsOut | CompatPrefsOut | null): Record<string, unknown> | undefined {
  if (!p) return undefined;
  const out: Record<string, unknown> = {};
  if (Array.isArray(p.allergies) && p.allergies.length > 0) out.allergies = p.allergies.slice(0, 10);
  if (p.sensitivity !== 'likely') out.sensitivity = p.sensitivity;
  if (p.acne_prone) out.acne_prone = true;
  if (p.ethical_mode !== 'off') out.ethical_mode = p.ethical_mode;
  if (p.cautious_actives) out.cautious_actives = true;
  return Object.keys(out).length > 0 ? out : undefined;
}

function compactJobs(jobs: TrackedJob[]): Record<string, unknown>[] {
  return jobs.slice(0, 5).map((j) => {
    const o: Record<string, unknown> = {
      id: j.id,
      kind: j.kind,
      status: j.status,
    };
    if (j.label) o.label = j.label;
    if (j.queue !== undefined && j.queue !== null) o.queue = j.queue;
    return o;
  });
}

/**
 * Build the per-turn dynamic context block.
 *
 * The result is meant to be PREPENDED to the current user message ONLY (never
 * persisted in `historyRef`), so the static system prompt's KV cache is not
 * invalidated and old contexts don't accumulate across turns.
 */
export function buildDynamicContext(input: DynamicContextInput): string {
  const now = input.now ?? new Date();
  const ctx: Record<string, unknown> = {
    time: {
      iso: now.toISOString(),
      // Hour & weekday give the model enough cue for routine timing without re-encoding much.
      local: now.toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      }),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    plan: input.plan,
  };

  if (input.user) {
    const name = input.user.name ?? (input.user.email ? input.user.email.split('@')[0] : undefined);
    if (name) ctx.user = { name };
  }

  const scanner = compactPrefs(input.scannerPrefs);
  const compat = compactPrefs(input.compatPrefs);
  if (scanner || compat) {
    ctx.prefs = {
      ...(scanner ? { scanner } : {}),
      ...(compat ? { compat } : {}),
    };
  }

  if (input.jobs.length > 0) {
    ctx.active_jobs = compactJobs(input.jobs);
  }

  return `<context>${JSON.stringify(ctx)}</context>\n\n`;
}
